import styled from "@emotion/styled";
import {
  IMessageDataReminder,
  ISendMessagePayload,
  SEND_MESSAGE_TYPE,
  SOCKET_BODY_EMOJI,
} from "@home-health-notify/domain";
import { Button, TextField, Typography } from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "hhn-commons";
import { useEffect, useState } from "react";
import {
  FormControlLayout,
  InputField,
} from "../../addPatientGroup/AddPatientGroup.style";
import CommonDialog from "../../common/CommonDialog/CommonDialog";
import { timePickerViewRenderers } from "../../common/ViewRenderers/timePickerViewRenderers";

const ReminderPickerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;
const MessageInputField = styled(TextField)`
  width: 100%;
  margin-bottom: 5px;
`;
const WordLimit = styled(Typography)`
  font-size: x-small;
  align-self: flex-end;
`;

interface ReminderProps {
  isOpen: boolean;
  onClose: () => void;

  sendMessage(
    payload: ISendMessagePayload,
    sendMessageType: SEND_MESSAGE_TYPE,
  ): void;
}

export const Reminder = ({ isOpen, onClose, sendMessage }: ReminderProps) => {
  const { t } = useTranslation();
  const tNs = (key: string): string => t(key, { ns: "message" });

  const currentTime = dayjs();
  const [titleErrorMessage, setTitleErrorMessage] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState(false);
  const [timeErrorMessage, setTimeErrorMessage] = useState(false);
  const [messageErrorMessage, setMessageErrorMessage] = useState(false);

  const [title, setTitle] = useState<string>("");
  const [reminderDate, setReminderDate] = useState<Dayjs | null>(null);
  const [reminderTime, setReminderTime] = useState<Dayjs | null>(null);
  const [message, setMessage] = useState<string>("");

  const [isTodayDate, setIsTodayDate] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const [reminderData, setReminderData] = useState<
    IMessageDataReminder | undefined
  >(undefined);

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setTitleErrorMessage(false);
    setDateErrorMessage(false);
    setTimeErrorMessage(false);
    setMessageErrorMessage(false);

    setTitle("");
    setReminderDate(null);
    setReminderTime(null);
    setMessage("");

    setIsTodayDate(false);
    setIsDatePickerOpen(false);
    setIsTimePickerOpen(false);

    setReminderData({
      title: "",
      schedulerDate: 0,
      schedulerTime: 0,
      message: "",
      isReminder: false,
    });
  };

  const validateReminderFields = () => {
    return (
      validateReminderFormData() ||
      titleErrorMessage ||
      dateErrorMessage ||
      timeErrorMessage ||
      messageErrorMessage
    );
  };

  const validateReminderFormData = () => {
    return (
      !reminderData?.title ||
      !reminderData?.schedulerDate ||
      !reminderData?.schedulerTime ||
      !reminderData?.message
    );
  };

  const onChangeReminderFields = (key: string, value: any) => {
    setReminderData(prev => {
      const newReminderData = {
        ...(prev ?? {
          title: "",
          schedulerDate: 0,
          schedulerTime: 0,
          message: "",
          isReminder: false,
        }),
      };
      if (key === "title") {
        newReminderData.title = value;
      } else if (key === "message") {
        newReminderData.message = value;
      } else if (key === "schedulerDate") {
        newReminderData.schedulerDate = value;
      } else if (key === "schedulerTime") {
        newReminderData.schedulerTime = value;
      } else {
        return {
          ...newReminderData,
          [key]: value,
          isReminder: newReminderData.isReminder ?? false,
        };
      }
      newReminderData.isReminder = newReminderData.isReminder ?? false;
      return newReminderData;
    });
  };

  const onSendReminderButton = () => {
    onSendReminder();
    onClose();
  };

  const onSendReminder = () => {
    let sendChatMessage: ISendMessagePayload = {
      body: SOCKET_BODY_EMOJI.REMINDER,
    };
    if (reminderData) {
      sendChatMessage.reminder = reminderData;
    }
    sendMessage(sendChatMessage, SEND_MESSAGE_TYPE.REMINDER);
  };

  const onSetReminderTitle = (title: string) => {
    setTitle(title);
    setTitleErrorMessage(title.trim().length <= 0);
    onChangeReminderFields("title", title);
  };

  const onSetReminderMessage = (message: string) => {
    setMessage(message);
    setMessageErrorMessage(message.trim().length <= 0);
    onChangeReminderFields("message", message);
  };

  const onSetReminderDate = (dateValue: Dayjs | null) => {
    if (!dateValue) {
      setReminderDate(null);
      onChangeReminderFields("schedulerDate", undefined);
      setDateErrorMessage(true);
      setIsTodayDate(false);
      setTimeErrorMessage(true);
      return;
    }
    const currentDate = dayjs();
    const selectedDate = dayjs(dateValue);
    setReminderDate(dateValue);

    const dateFormatted = new Date(
      dateValue.year(),
      dateValue.month(),
      dateValue.date(),
      0,
      0,
      0,
    ).getTime();
    onChangeReminderFields("schedulerDate", dateFormatted);
    setDateErrorMessage(false);

    if (selectedDate.isSame(currentDate, "day")) {
      setReminderTime(currentTime);
      setIsTodayDate(true);
      setTimeErrorMessage(false);
      const currentDateTime = dayjs(new Date());
      const datetimeFormatted = new Date(
        currentDateTime.year(),
        currentDateTime.month(),
        currentDateTime.date(),
        currentDateTime.hour(),
        currentDateTime.minute(),
        0,
      ).getTime();
      if (reminderData) reminderData.schedulerTime = datetimeFormatted;
    } else {
      setReminderTime(null);
      setIsTodayDate(false);
      setTimeErrorMessage(true);
      if (reminderData) reminderData.schedulerTime = 0;
    }
  };

  const onSetReminderTime = (timeValue: Dayjs | null) => {
    setReminderTime(timeValue);
    const reminderDateSelected = reminderData?.schedulerDate
      ? dayjs(reminderData.schedulerDate)
      : dayjs(new Date());
    if (!timeValue) {
      onChangeReminderFields("schedulerTime", undefined);
      setTimeErrorMessage(true);
      return;
    }
    const timeFormatted = new Date(
      reminderDateSelected.year(),
      reminderDateSelected.month(),
      reminderDateSelected.date(),
      timeValue.hour(),
      timeValue.minute(),
      0,
    ).getTime();
    onChangeReminderFields("schedulerTime", timeFormatted);
    setTimeErrorMessage(false);
  };

  return (
    <CommonDialog
      isDialogOpen={isOpen}
      onDialogClose={onClose}
      title={tNs("Reminder")}
      titleAlignment="center"
      maxWidth="sm"
      actionContent={
        <>
          <Button
            autoFocus
            onClick={onSendReminderButton}
            disabled={validateReminderFields()}
            variant="contained"
            id="send-reminder"
            data-testid="send-reminder"
          >
            {t("buttonLabels.done")}
          </Button>
        </>
      }
    >
      <FormControlLayout>
        <InputField
          fullWidth
          required
          variant="standard"
          id="reminder-title"
          data-testid="reminder-title"
          slotProps={{
            htmlInput: { "data-testid": "reminder-title" },
          }}
          label={tNs("Title")}
          type="text"
          value={title}
          error={titleErrorMessage}
          helperText={titleErrorMessage ? tNs("reminder.invalidTitle") : " "}
          onChange={e => {
            onSetReminderTitle(e.target.value);
          }}
        />
        <ReminderPickerWrapper>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              open={isDatePickerOpen}
              onOpen={() => setIsDatePickerOpen(true)}
              onClose={() => setIsDatePickerOpen(false)}
              label={tNs("Date")}
              value={reminderDate || null}
              format={"MM/DD/YYYY"}
              onChange={value => onSetReminderDate(value)}
              minDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  variant: "standard",
                  id: "reminder-date-picker",
                  slotProps: {
                    htmlInput: {
                      "data-testid": "reminder-date-picker",
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  },
                  error: dateErrorMessage,
                  helperText: dateErrorMessage
                    ? tNs("reminder.invalidDate")
                    : " ",
                  onClick: () => {
                    setIsTimePickerOpen(false);
                    setIsDatePickerOpen(true);
                  },
                  onKeyDown: event => {
                    if (event.key !== "Tab") {
                      event.preventDefault();
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              viewRenderers={timePickerViewRenderers}
              open={isTimePickerOpen}
              onOpen={() => setIsTimePickerOpen(true)}
              onClose={() => setIsTimePickerOpen(false)}
              label={tNs("Time")}
              value={reminderTime}
              format={"hh:mm"}
              onChange={onSetReminderTime}
              minTime={isTodayDate ? dayjs() : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  variant: "standard",
                  id: "reminder-time-picker",
                  error: timeErrorMessage,
                  helperText: timeErrorMessage
                    ? tNs("reminder.invalidTime")
                    : " ",
                  onClick: () => {
                    setIsDatePickerOpen(false);
                    setIsTimePickerOpen(true);
                  },
                  onKeyDown: event => {
                    if (event.key !== "Tab") {
                      event.preventDefault();
                    }
                  },
                  slotProps: {
                    htmlInput: {
                      "data-testid": "reminder-time-picker",
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                    input: {
                      readOnly: true,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </ReminderPickerWrapper>
        <MessageInputField
          required
          variant="standard"
          id="reminder-message"
          data-testid="reminder-message"
          label={tNs("Message")}
          type="text"
          value={message}
          multiline
          rows={2}
          error={messageErrorMessage}
          helperText={
            messageErrorMessage ? tNs("reminder.invalidMessage") : " "
          }
          onChange={e => {
            onSetReminderMessage(e.target.value);
          }}
          slotProps={{
            htmlInput: { "data-testid": "reminder-message", maxLength: 250 },
          }}
        />
        <WordLimit>{message.length}/250</WordLimit>
      </FormControlLayout>
    </CommonDialog>
  );
};

export default Reminder;
